import { FiCheck } from 'react-icons/fi'
import { PlanHighlightsProps } from './types'

export const PlanHighlights = ({
	selectedPlan,
	features
}: PlanHighlightsProps) => {
	const renderFeatureLabel = (label: string) => {
		const parts = label.split('**')

		if (parts.length === 1) return label

		return parts.map((part, index) =>
			index % 2 === 1 ? (
				<span key={index} className="font-bold">
					{part}
				</span>
			) : (
				part
			)
		)
	}

	const isProOrCustom = selectedPlan.id === 'pro' || selectedPlan.id === 'custom'

	// Split features into two columns
	const midpoint = Math.ceil(features.length / 2)
	const leftFeatures = features.slice(0, midpoint)
	const rightFeatures = features.slice(midpoint)

	return (
		<div className="bg-[var(--lavender-fill-soft,rgba(238,237,254,0.5))] border border-t-card-border rounded-[14px] px-[20px] py-[16px] flex flex-col gap-[20px]">
			{/* Title + Subtitle */}
			<div className="flex flex-col gap-[4px]">
				<p className="font-grotesk font-medium text-[16px] leading-[22px] text-t-base-blue">
					{selectedPlan.name} plan highlights
				</p>
				<p className="font-grotesk text-[12px] leading-[16px] text-t-coupon">
					{selectedPlan.description}
				</p>
			</div>

			{/* Features - 2 column grid */}
			<div className="flex flex-col xl:flex-row gap-[8px]">
				<div className="flex-1 min-w-0 flex flex-col gap-[8px]">
					{leftFeatures.map((feature, idx) => (
						<div key={idx} className="flex items-center gap-[8px]">
							<div className="flex items-center pt-[2px] flex-shrink-0">
								{feature.available ? (
									<FiCheck className="w-4 h-4 text-brand-tint-text" />
								) : (
									<svg
										className="text-brand-tint-text"
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										viewBox="0 0 13 14"
										fill="none"
									>
										<path
											d="M9.41667 5.41667V4.08333C9.41667 2.24238 7.92428 0.75 6.08333 0.75C4.24238 0.75 2.75 2.24238 2.75 4.08333V5.41667M6.08333 8.41667V9.75M3.95 12.75H8.21667C9.33677 12.75 9.89682 12.75 10.3246 12.532C10.701 12.3403 11.0069 12.0343 11.1987 11.658C11.4167 11.2302 11.4167 10.6701 11.4167 9.55V8.61667C11.4167 7.49656 11.4167 6.93651 11.1987 6.50869C11.0069 6.13236 10.701 5.8264 10.3246 5.63465C9.89682 5.41667 9.33677 5.41667 8.21667 5.41667H3.95C2.8299 5.41667 2.26984 5.41667 1.84202 5.63465C1.46569 5.8264 1.15973 6.13236 0.967987 6.50869C0.75 6.93651 0.75 7.49656 0.75 8.61667V9.55C0.75 10.6701 0.75 11.2302 0.967987 11.658C1.15973 12.0343 1.46569 12.3403 1.84202 12.532C2.26984 12.75 2.8299 12.75 3.95 12.75Z"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								)}
							</div>
							<span className="font-grotesk text-[14px] leading-[18px] text-t-default">
								{renderFeatureLabel(feature.label)}
								{!feature.available && feature.requiredPlan && (
									<span className="font-bold">
										{' '}
										({feature.requiredPlan} plan)
									</span>
								)}
							</span>
						</div>
					))}
				</div>
				<div className="flex-1 min-w-0 flex flex-col gap-[8px]">
					{rightFeatures.map((feature, idx) => (
						<div key={idx} className="flex items-center gap-[8px]">
							<div className="flex items-center pt-[2px] flex-shrink-0">
								{feature.available ? (
									<FiCheck className="w-4 h-4 text-brand-tint-text" />
								) : (
									<svg
										className="text-brand-tint-text"
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										viewBox="0 0 13 14"
										fill="none"
									>
										<path
											d="M9.41667 5.41667V4.08333C9.41667 2.24238 7.92428 0.75 6.08333 0.75C4.24238 0.75 2.75 2.24238 2.75 4.08333V5.41667M6.08333 8.41667V9.75M3.95 12.75H8.21667C9.33677 12.75 9.89682 12.75 10.3246 12.532C10.701 12.3403 11.0069 12.0343 11.1987 11.658C11.4167 11.2302 11.4167 10.6701 11.4167 9.55V8.61667C11.4167 7.49656 11.4167 6.93651 11.1987 6.50869C11.0069 6.13236 10.701 5.8264 10.3246 5.63465C9.89682 5.41667 9.33677 5.41667 8.21667 5.41667H3.95C2.8299 5.41667 2.26984 5.41667 1.84202 5.63465C1.46569 5.8264 1.15973 6.13236 0.967987 6.50869C0.75 6.93651 0.75 7.49656 0.75 8.61667V9.55C0.75 10.6701 0.75 11.2302 0.967987 11.658C1.15973 12.0343 1.46569 12.3403 1.84202 12.532C2.26984 12.75 2.8299 12.75 3.95 12.75Z"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								)}
							</div>
							<span className="font-grotesk text-[14px] leading-[18px] text-t-default">
								{renderFeatureLabel(feature.label)}
								{!feature.available && feature.requiredPlan && (
									<span className="font-bold">
										{' '}
										({feature.requiredPlan} plan)
									</span>
								)}
							</span>
						</div>
					))}
				</div>
			</div>

			{/* Divider + Note - hidden for Pro/Custom */}
			{!isProOrCustom && (
				<>
					<div className="w-full h-px bg-t-card-border" />
					<p className="font-grotesk text-[12px] leading-[16px] text-t-desc-gray">
						Note: all Pro features are accessible during Free trial
					</p>
				</>
			)}
		</div>
	)
}
